import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { formatPhoneNumber } from '../../Utils/phoneUtils';

import DefaultProfile from '../../image/DefaultProfile.png';

export default function MainAccountInfoCard(props) {
  const {
    id,
    email,
    ownerName,
    ownerProfile,
    phone,
    phoneCountryCode,
    passwordModifiedAt,
    secondPasswordModifiedAt,
    onClickEditPhone,
    onClickEditPassword,
    onClickEditSecondPassword
  } = props;

  const { t, languageMode } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 마지막 변경일 날짜 포맷팅 //
  function formatModifiedAt(value) {
    if (value === undefined || value === null || value === '') return t('myStoreSetting.account.field.noChange');
    return moment(value).format('YY.MM.DD HH:mm');
  }

  // 휴대전화 표시 포맷팅 //
  function getDisplayPhone() {
    if (phone === undefined || phone === '') return t('myStoreSetting.account.field.noChange');

    return formatPhoneNumber(phone, phoneCountryCode || '+82', languageMode);
  }

  // 상단 행 - 프로필 + 이름·역할·이메일 + 상태 pill //
  function renderTopRow() {
    return (
      <TopRow>
        <ProfileImage src={ownerProfile || DefaultProfile} alt='profile' />
        <ProfileInfoColumn>
          <NameRow>
            <NameText color={theme.text}>{ownerName || ''}</NameText>
            <NameDot backgroundColor={theme.primary} />
            <RoleText color={theme.text}>{t('myStoreSetting.account.role.manager')}</RoleText>
          </NameRow>
          <EmailText color={theme.text}>{email || ''}</EmailText>
        </ProfileInfoColumn>
        <StatusChip borderColor={theme.primary} backgroundColor={theme.primaryAlpha20}>
          <StatusText color={theme.text}>{t('myStoreSetting.account.status.activeOwner')}</StatusText>
        </StatusChip>
      </TopRow>
    );
  }

  // ID, 휴대전화 셀 //
  function renderFieldCell(label, value, actionLabel, onClickAction, isActionHidden) {
    return (
      <FieldCell>
        <FieldHeaderRow>
          <FieldLabelText color={theme.subText}>{label}</FieldLabelText>
          <ActionText
            color={theme.primary}
            isHidden={isActionHidden}
            onClick={isActionHidden ? undefined : onClickAction}>
            {actionLabel}
          </ActionText>
        </FieldHeaderRow>
        <FieldValueText color={theme.text}>{value}</FieldValueText>
      </FieldCell>
    );
  }

  // 패스워드 셀 (마지막 변경 + 일자) //
  function renderPasswordCell(label, lastChangeDate, onClickAction) {
    return (
      <FieldCell>
        <FieldHeaderRow>
          <FieldLabelText color={theme.subText}>{label}</FieldLabelText>
          <ActionText
            color={theme.primary}
            isHidden={false}
            onClick={onClickAction}>
            {t('myStoreSetting.account.field.change')}
          </ActionText>
        </FieldHeaderRow>
        <PasswordValueRow>
          <FieldValueText color={theme.text}>{t('myStoreSetting.account.field.lastChange')}</FieldValueText>
          <PasswordDateText color={theme.text}>{lastChangeDate}</PasswordDateText>
        </PasswordValueRow>
      </FieldCell>
    );
  }

  return (
    <Card backgroundColor={theme.subBackground}>
      {renderTopRow()}
      <HorizontalDivider backgroundColor={theme.line} />
      <FieldRow>
        {renderFieldCell(t('myStoreSetting.account.loginId'), id || '', t('myStoreSetting.account.field.modify'), undefined, true)}
        <VerticalDivider backgroundColor={theme.line} />
        {renderFieldCell(t('myStoreSetting.account.phone'), getDisplayPhone(), t('myStoreSetting.account.field.modify'), onClickEditPhone, false)}
      </FieldRow>
      <FieldRow>
        {renderPasswordCell(t('myStoreSetting.account.password'), formatModifiedAt(passwordModifiedAt), onClickEditPassword)}
        <VerticalDivider backgroundColor={theme.line} />
        {renderPasswordCell(t('myStoreSetting.account.secondPassword'), formatModifiedAt(secondPasswordModifiedAt), onClickEditSecondPassword)}
      </FieldRow>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const ProfileImage = styled.img`
  width: 62px;
  height: 62px;

  border-radius: 5px;

  object-fit: cover;

  flex-shrink: 0;
`;

const ProfileInfoColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;

  flex: 1 0 0;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const NameText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const NameDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};
`;

const RoleText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const EmailText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const StatusChip = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  min-width: 110px;
  height: 21px;

  padding: 5px 10px;
  box-sizing: border-box;

  border-radius: 100px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const StatusText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const HorizontalDivider = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};

  margin: 10px 0;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: stretch;

  gap: 20px;

  width: 100%;
`;

const FieldCell = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: stretch;

  gap: 5px;

  height: 47px;

  flex: 1 0 0;
  min-width: 0;
`;

const FieldHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  width: 100%;
`;

const FieldLabelText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const ActionText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;

  opacity: ${props => props.isHidden ? 0 : 1};
  pointer-events: ${props => props.isHidden ? 'none' : 'auto'};

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.isHidden ? 'default' : 'pointer'};
    }
  }
`;

const FieldValueText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const PasswordValueRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const PasswordDateText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const VerticalDivider = styled.div`
  justify-content: center;
  align-items: center;

  width: 1px;
  height: 36px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;
